import React, { useEffect, useState } from "react";
import {
  useLogger,
  DeviceLabels,
} from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../providers/DevicesProvider";
import { logVideoRoomError } from "../utils/logVideoRoomError";
import { AUDIO_CONSTRAINTS } from "../utils/audioConstraints";

export const DevicePermissionInitializer: React.FC = () => {
  const logger = useLogger();
  const {
    listAndSelectDevices,
    setPermissionDenied,
  } = useDevices();
  const [permissionsRequested, setPermissionsRequested] = useState(false);

  useEffect(() => {
    const requestPermissionsDirectly = async () => {
      if (permissionsRequested) {
        return;
      }

      try {
        setPermissionsRequested(true);

        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: AUDIO_CONSTRAINTS,
        });

        stream.getTracks().forEach((track) => track.stop());

        // Retry-based enumeration: poll until devices with labels appear
        const MAX_RETRIES = 3;
        const RETRY_INTERVAL_MS = 200;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          await new Promise((r) => setTimeout(r, RETRY_INTERVAL_MS));
          try {
            await listAndSelectDevices(DeviceLabels.AudioAndVideo);

            // Check if we got labelled devices (labels require permission)
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasLabels = devices.some((d) => d.label && d.label.trim() !== "");
            if (hasLabels) break;
          } catch (err) {
            logger.error(`Error in device initialization (attempt ${attempt + 1}): ${err}`);
            if (attempt === MAX_RETRIES - 1) {
              logVideoRoomError(
                "Device initialization failed after retries",
                "device_init_retry_exhausted",
                { attempts: MAX_RETRIES, error: String(err) }
              );
            }
          }
        }

        // Auto-selection is handled by DeviceManager.listAndSelectDevices
        // which picks the first available device when none is selected.

      } catch (err) {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setPermissionDenied(true);
        }
        logger.error(`Error requesting device permissions: ${err}`);
        logVideoRoomError(
          "Device permission request failed",
          err instanceof DOMException ? err.name : "permission_error",
          { error: String(err) }
        );

        // Still enumerate devices so isDevicesLoading becomes false
        // and the UI can show the appropriate error message
        try {
          await listAndSelectDevices(DeviceLabels.AudioAndVideo);
        } catch {
          // Non-fatal: device enumeration may fail without permissions
        }
      }
    };

    // Call immediately when component mounts
    requestPermissionsDirectly();
  }, [
    permissionsRequested,
    listAndSelectDevices,
    logger,
  ]);

  // This component doesn't render anything visual
  return null;
};
